import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import NombreCliente from '../../components/NombreCliente';
import { money, moneyK, mesLabel, hoy, Title, Empty } from './ui';
import DetalleCobros from './DetalleCobros';
import SerieFacturacion, { armarSerie } from './SerieFacturacion';
import RangoFechas, { rangoInicial } from '../../components/RangoFechas';

const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };
const tick = { fill: 'var(--chart-text)', fontSize: 11 };

// Tooltip propio: además de cada barra, muestra el total facturado del mes
function TooltipMes({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload || {};
  const facturado = Number(d.cobrado || 0) + Number(d.pendiente || 0);
  return (
    <div style={{ ...tooltipStyle, padding: '10px 12px', fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 6, textTransform: 'capitalize' }}>{label}</div>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Facturado: {money(facturado)}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {money(p.value)}</div>
      ))}
    </div>
  );
}

export default function AgenciaFinanzas({ embebido = false, rango: rangoExterno = null }) {
  const [preset, setPreset] = useState(() => rangoInicial('actual'));
  const [data, setData] = useState(null);
  const [indices, setIndices] = useState({ cotizaciones: [], inflacion: [] });
  const [cot, setCot] = useState({ fecha: hoy(), oficial: '', blue: '', mep: '' });
  const [inf, setInf] = useState({ mes: hoy().slice(0, 7), porcentaje: '' });
  const [moneda, setMoneda] = useState('ars');
  const [detalle, setDetalle] = useState(null);   // qué números estamos mirando en detalle

  // el rango propio ya viene calculado por el selector, igual que en Reportes y Cobros
  const rangoPropio = preset;
  // ojo: el rango se arma con fechas sueltas, no con el objeto, porque un objeto
  // nuevo en cada render hacía que el efecto se dispare sin parar
  const desde = rangoExterno ? rangoExterno.desde : rangoPropio.desde;
  const hasta = rangoExterno ? rangoExterno.hasta : rangoPropio.hasta;
  const rango = useMemo(() => ({ desde, hasta }), [desde, hasta]);

  const verDetalle = (grupo, mesRaw) => {
    if (detalle && detalle.grupo === grupo && detalle.mes === mesRaw) return setDetalle(null);
    if (!mesRaw) return setDetalle({ grupo, desde, hasta });
    const [a, m] = mesRaw.split('-');
    const ultimo = new Date(Date.UTC(Number(a), Number(m), 0)).toISOString().slice(0, 10);
    setDetalle({ grupo, mes: mesRaw, etiqueta: mesLabel(mesRaw), desde: `${mesRaw}-01`, hasta: ultimo });
  };

  /* Mientras trae los datos nuevos se siguen viendo los viejos, apenas
     atenuados. Sin ninguna señal, cambiar de período parecía no hacer nada. */
  const [actualizando, setActualizando] = useState(false);
  const cargar = useCallback(() => {
    setActualizando(true);
    api.get('/agency/reportes', { params: { desde, hasta } })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setActualizando(false));
    api.get('/agency/indices').then(r => setIndices(r.data)).catch(() => {});
  }, [desde, hasta]);
  useEffect(() => { cargar(); setDetalle(null); }, [cargar]);

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const cotPorMes = Object.fromEntries((data.cotizaciones || []).map(c => [c.mes, c]));
  const inflPorMes = Object.fromEntries((data.inflacion || []).map(i => [i.mes, Number(i.porcentaje)]));
  const enUsd = moneda === 'usd';
  // el más bajo de los que se hayan cargado ese mes
  const tcDelMes = (mesRaw) => {
    const c = cotPorMes[mesRaw] || {};
    const valores = [c.oficial_min, c.blue_min, c.mep_min].map(Number).filter(v => v > 0);
    return valores.length ? Math.min(...valores) : null;
  };
  const convertir = (m) => {
    const tc = tcDelMes(m.mesRaw);
    if (!tc) return null;
    const out = { mes: m.mes, mesRaw: m.mesRaw, tc };
    Object.entries(m).forEach(([k, v]) => { if (typeof v === 'number') out[k] = Math.round(v / tc); });
    return out;
  };
  const serieArs = armarSerie(data.por_mes).map(m => ({ ...m, inflacion: inflPorMes[m.mesRaw] ?? null }));
  const sinCotizacion = enUsd ? serieArs.filter(m => !tcDelMes(m.mesRaw)).length : 0;
  const serie = enUsd ? serieArs.map(convertir).filter(Boolean) : serieArs;
  // formato según la moneda elegida
  const fmt = (v) => (enUsd ? 'USD ' + Math.round(Number(v) || 0).toLocaleString('es-AR') : money(v));
  const fmtCorto = (v) => (enUsd ? 'u$s ' + Math.round((Number(v) || 0) / 1000) + 'k' : moneyK(v));
  const egresosPorMes = Object.fromEntries((data.egresos_por_mes || []).map(e => [e.mes, Number(e.total)]));
  const serieConEgresos = serie.map(s => {
    const eg = egresosPorMes[s.mesRaw] || 0;
    const egresos = enUsd ? Math.round(eg / (s.tc || 1)) : eg;
    return { ...s, egresos, resultado: s.cobrado - egresos };
  });
  const totalCobrado = serie.reduce((a, s) => a + s.cobrado, 0);
  const totalFacturado = serie.reduce((a, s) => a + s.cobrado + s.pendiente, 0);
  const totalPendiente = serie.reduce((a, s) => a + s.pendiente, 0);
  const totalAPasar = serie.reduce((a, s) => a + s.a_pasar, 0);

  const totalEgresosMostrado = serieConEgresos.reduce((a, s) => a + s.egresos, 0);

  const guardarCot = async (e) => {
    e.preventDefault();
    try { await api.post('/agency/indices/cotizacion', cot); toast.success('Cotización guardada'); setCot({ ...cot, oficial: '', blue: '', mep: '' }); cargar(); }
    catch { toast.error('Error'); }
  };
  const guardarInf = async (e) => {
    e.preventDefault();
    try { await api.post('/agency/indices/inflacion', inf); toast.success('Inflación guardada'); setInf({ ...inf, porcentaje: '' }); cargar(); }
    catch { toast.error('Error'); }
  };

  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' };
  const td = { padding: '8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const monedaChips = (
    <>
      <button type="button" className={'chip-periodo' + (moneda === 'ars' ? ' activo' : '')}
        onClick={() => setMoneda('ars')}>En pesos</button>
      <button type="button" className={'chip-periodo' + (moneda === 'usd' ? ' activo' : '')}
        onClick={() => setMoneda('usd')}>En dólares</button>
    </>
  );

  return (
    <div style={{ opacity: actualizando ? 0.55 : 1, transition: 'opacity 0.15s' }}>
      {!embebido && <Title>Finanzas</Title>}

      {rangoExterno ? (
        <div className="filtro-fechas" style={{ marginBottom: 16 }}>
          <div className="filtro-fechas-fila">{monedaChips}</div>
        </div>
      ) : (
        <RangoFechas valor={preset} onChange={setPreset} derecha={monedaChips} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="card card-dato" style={{ padding: '14px 16px' }} title="Lo que corresponde al período, se haya cobrado o no">
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Facturado</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{fmtCorto(totalFacturado)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {totalFacturado ? `${Math.round((totalCobrado / totalFacturado) * 100)}% ya cobrado` : 'sin movimientos'}</div></div>
        <div className="card card-dato" style={{ padding: '14px 16px', cursor: 'pointer' }} onClick={() => verDetalle('pagado')}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cobrado</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>{fmtCorto(totalCobrado)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>ya entró · ver detalle</div></div>
        <div className="card card-dato" style={{ padding: '14px 16px', cursor: 'pointer' }} onClick={() => verDetalle('abiertos')}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Pendiente de cobro</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{fmtCorto(totalPendiente)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>se lo pasamos y no pagó · ver detalle</div></div>
        <div className="card card-dato" style={{ padding: '14px 16px', cursor: 'pointer' }} onClick={() => verDetalle('a_pasar')}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>A pasar</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#64748b' }}>{fmtCorto(totalAPasar)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>cuota futura · ver detalle</div></div>
        {/* Lo que todavía no existe como cobro: la recurrencia del mes que viene.
            Va al lado de "A pasar" porque son las dos caras de lo mismo: plata
            contabilizada que todavía no entró, y plata que ni siquiera se emitió. */}
        {data.proximo_mes && (
          <div className="card card-dato" style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>A facturar el mes próximo</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent)' }}>{fmtCorto(data.proximo_mes.total)}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {data.proximo_mes.abonos_cant} abonos
              {data.proximo_mes.hosting_cant > 0 && ` · ${data.proximo_mes.hosting_cant} hosting`}
              {' · sin los pausados'}
            </div>
          </div>
        )}
        <div className="card card-dato" style={{ padding: '14px 16px' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Egresos</div><div style={{ fontSize: 24, fontWeight: 800, color: '#ef4444' }}>{fmtCorto(totalEgresosMostrado)}</div></div>
        <div className="card card-dato" style={{ padding: '14px 16px' }}><div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Resultado</div><div style={{ fontSize: 24, fontWeight: 800, color: '#22c55e' }}>{fmtCorto(totalCobrado - totalEgresosMostrado)}</div></div>
      </div>

      {detalle && (
        <DetalleCobros grupo={detalle.grupo} desde={detalle.desde} hasta={detalle.hasta}
          etiqueta={detalle.etiqueta} onClose={() => setDetalle(null)} />
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Facturación por mes</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Una columna por categoría en cada mes. El tono fuerte es lo cobrado y el claro lo que falta cobrar.
          {enUsd
            ? ` Convertido con la cotización más baja cargada en cada mes${sinCotizacion ? `; ${sinCotizacion} ${sinCotizacion === 1 ? 'mes queda afuera por no tener cotización' : 'meses quedan afuera por no tener cotización'}` : ''}.`
            : ' Tocá una barra para ver el detalle de ese mes.'}
        </div>
        {serie.length ? (
          <SerieFacturacion datos={serieConEgresos} onElegir={enUsd ? undefined : verDetalle} fmt={fmt} fmtCorto={fmtCorto} extra={
            <Bar dataKey="egresos" name="Egresos" fill="#ef4444" radius={[5, 5, 0, 0]} />
          } />
        ) : <Empty>Sin datos en el período</Empty>}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Mes a mes</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          El total es lo que corresponde a cada mes, se haya cobrado o no: los abonos a mes vencido y los que se demoran
          siguen contando en el mes que les toca. Tocá una fila para ver el detalle.
        </div>
        <div className="table-container">
          <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Mes</th>
              <th style={{ ...th, textAlign: 'right' }}>Facturado</th>
              <th style={{ ...th, textAlign: 'right' }}>Cobrado</th>
              <th style={{ ...th, textAlign: 'right' }}>Pendiente</th>
              <th style={{ ...th, textAlign: 'right' }}>A pasar</th>
              <th style={{ ...th, textAlign: 'right' }}>Entró</th>
            </tr></thead>
            <tbody>{[...serie].reverse().map(m => {
              const facturado = m.cobrado + m.pendiente;
              const pct = facturado ? Math.round((m.cobrado / facturado) * 100) : null;
              return (
                <tr key={m.mesRaw}>
                  <td style={{ ...td, fontWeight: 600, textTransform: 'capitalize' }}>{m.mes}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmt(facturado)}</td>
                  <td style={{ ...td, textAlign: 'right', color: '#22c55e', cursor: 'pointer' }} onClick={() => verDetalle('pagado', m.mesRaw)}>{fmt(m.cobrado)}</td>
                  <td style={{ ...td, textAlign: 'right', color: m.pendiente ? '#f59e0b' : 'var(--text-muted)', cursor: 'pointer' }} onClick={() => verDetalle('abiertos', m.mesRaw)}>{m.pendiente ? fmt(m.pendiente) : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--text-muted)', cursor: 'pointer' }} onClick={() => verDetalle('a_pasar', m.mesRaw)}>{m.a_pasar ? fmt(m.a_pasar) : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontSize: 12, color: 'var(--text-muted)' }}>{pct === null ? '—' : pct + '%'}</td>
                </tr>
              );
            })}</tbody>
          </table></div>
        </div>
      </div>

      {serie.some(s => s.inflacion !== null) && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Facturación vs. inflación</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Variación mensual de lo cobrado comparada con la inflación que cargaste</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={serie.map((s, i) => ({ ...s, variacion: i > 0 && serie[i - 1].cobrado ? Math.round(((s.cobrado / serie[i - 1].cobrado) - 1) * 1000) / 10 : null }))} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={tick} />
              <YAxis tick={tick} tickFormatter={(v) => v + '%'} width={50} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [v + '%', n]} />
              <Legend wrapperStyle={{ fontSize: 12, color: 'var(--chart-text)' }} />
              <Line type="monotone" dataKey="variacion" name="Tu facturación" stroke="var(--accent)" strokeWidth={2} />
              <Line type="monotone" dataKey="inflacion" name="Inflación" stroke="#ef4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Por servicio</h3>
          <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{data.por_servicio.map(s => (
              <tr key={s.servicio}><td style={td}>{s.servicio}</td><td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{s.cant}</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(s.total)}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Top clientes</h3>
          <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{data.por_cliente.slice(0, 12).map(c => (
              <tr key={c.id}><td style={td}><NombreCliente nombre={c.nombre} logo={c.logo_url} /></td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.total)}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Por medio de pago</h3>
          <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{data.por_medio.map(m => (
              <tr key={m.medio}><td style={td}>{m.medio}</td><td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{m.cant}</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(m.total)}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
        {data.por_agencia.length > 0 && (
          <div className="card">
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Por agencia</h3>
            <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>{data.por_agencia.map(a => (
                <tr key={a.agencia}><td style={td}>{a.agencia}</td><td style={{ ...td, color: 'var(--text-muted)', fontSize: 12 }}>{a.clientes} clientes</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(a.total)}</td></tr>
              ))}</tbody>
            </table></div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 20 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Cargar cotización</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Se usa para ver la facturación en dólares. Queda el histórico.</div>
          <form onSubmit={guardarCot}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 8 }}>
              <div className="form-group"><label>Fecha</label><input type="date" value={cot.fecha} onChange={e => setCot({ ...cot, fecha: e.target.value })} required /></div>
              <div className="form-group"><label>Oficial</label><input type="number" step="0.01" value={cot.oficial} onChange={e => setCot({ ...cot, oficial: e.target.value })} /></div>
              <div className="form-group"><label>Blue</label><input type="number" step="0.01" value={cot.blue} onChange={e => setCot({ ...cot, blue: e.target.value })} /></div>
              <div className="form-group"><label>MEP</label><input type="number" step="0.01" value={cot.mep} onChange={e => setCot({ ...cot, mep: e.target.value })} /></div>
            </div>
            <button className="btn btn-primary btn-sm" type="submit"><Save size={14} /> Guardar</button>
          </form>
          <div className="table-container" style={{ border: 0, borderRadius: 0, marginTop: 12 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Fecha</th><th style={th}>Oficial</th><th style={th}>Blue</th><th style={th}>MEP</th></tr></thead>
            <tbody>{indices.cotizaciones.slice(0, 6).map(c => (
              <tr key={c.fecha}><td style={td}>{String(c.fecha).slice(0, 10).split('-').reverse().join('/')}</td>
                <td style={td}>{c.oficial || '—'}</td><td style={td}>{c.blue || '—'}</td><td style={td}>{c.mep || '—'}</td></tr>
            ))}</tbody>
          </table></div>
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Cargar inflación</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>El dato mensual del INDEC, para comparar con tu facturación.</div>
          <form onSubmit={guardarInf}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="form-group"><label>Mes</label><input type="month" value={inf.mes} onChange={e => setInf({ ...inf, mes: e.target.value })} required /></div>
              <div className="form-group"><label>Inflación %</label><input type="number" step="0.01" value={inf.porcentaje} onChange={e => setInf({ ...inf, porcentaje: e.target.value })} required /></div>
            </div>
            <button className="btn btn-primary btn-sm" type="submit"><Save size={14} /> Guardar</button>
          </form>
          <div className="table-container" style={{ border: 0, borderRadius: 0, marginTop: 12 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>{indices.inflacion.slice(0, 6).map(i => (
              <tr key={i.mes}><td style={td}>{mesLabel(i.mes)}</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{i.porcentaje}%</td></tr>
            ))}</tbody>
          </table></div>
        </div>
      </div>
    </div>
  );
}
